/**
 * CalendarAgentService — Agent de gestion du Calendrier de Tenues v2.
 *
 * Implémentation pure JavaScript déterministe (pas d'appel IA payant). Couvre
 * les 10 actions du spec :
 *   1. ADD_OUTFIT_TO_CALENDAR
 *   2. CHECK_DATE_CONFLICT
 *   3. GET_CALENDAR_MONTH_VIEW
 *   4. GET_DAY_DETAIL
 *   5. UPDATE_CALENDAR_ENTRY
 *   6. DELETE_CALENDAR_ENTRY
 *   7. GET_MONTH_STATS
 *   8. GENERATE_TOOLTIP_CONTENT
 *   9. SHOW_FIRST_TIME_HELP
 *  10. GENERATE_OUTFIT_PREVIEW_IMAGE
 *
 * Tier gating : client-side (lit `user.tier`). Free = 7j historique + pas de
 * stats avancées. Basic+ = stats. Premium+ = insights IA. Gold = full.
 *
 * v1.12.233
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  listMonth, getEntryDetail, createEntry, updateEntry, deleteEntry,
  addEntryWithOptionalPhoto,
} from './CalendarService';

// ─── Constantes ─────────────────────────────────────────────────────────
export const TIER_LIMITS = {
  free:    { history_days: 7,        stats: false, insights: false, ymRecap: false },
  basic:   { history_days: 30,       stats: true,  insights: false, ymRecap: false },
  premium: { history_days: Infinity, stats: true,  insights: true,  ymRecap: false },
  gold:    { history_days: Infinity, stats: true,  insights: true,  ymRecap: true  },
};

export const OCCASION_DISPLAY = {
  travail:   { emoji: '💼', color: '#1565C0', bg: '#E3F2FD',   label: 'Travail' },
  brunch:    { emoji: '🥐', color: '#D81B60', bg: '#FCE4EC',   label: 'Brunch' },
  soirée:    { emoji: '🍸', color: '#8E24AA', bg: '#F3E5F5',   label: 'Soirée' },
  soiree:    { emoji: '🍸', color: '#8E24AA', bg: '#F3E5F5',   label: 'Soirée' },
  sport:     { emoji: '🏃', color: '#2E7D32', bg: '#E8F5E9',   label: 'Sport' },
  cérémonie: { emoji: '💒', color: '#F57C00', bg: '#FFF3E0',   label: 'Cérémonie' },
  ceremonie: { emoji: '💒', color: '#F57C00', bg: '#FFF3E0',   label: 'Cérémonie' },
  casual:    { emoji: '👕', color: '#455A64', bg: '#ECEFF1',   label: 'Casual' },
  voyage:    { emoji: '✈️', color: '#00838F', bg: '#E0F7FA',   label: 'Voyage' },
  date:      { emoji: '❤️', color: '#C62828', bg: '#FFEBEE',   label: 'Date' },
  weekend:   { emoji: '☕', color: '#6D4C41', bg: '#EFEBE9',   label: 'Weekend' },
  quotidien: { emoji: '🚶', color: '#455A64', bg: '#ECEFF1',   label: 'Quotidien' },
  autre:     { emoji: '✨', color: '#9B7B5A', bg: '#FBF7EE',   label: 'Autre' },
};

const WEATHER_ICONS = {
  ensoleillé: '☀️', ensoleille: '☀️', sunny: '☀️',
  nuageux: '☁️', cloudy: '☁️', overcast: '☁️',
  partiellement: '⛅', 'partiellement_nuageux': '⛅', 'partly cloudy': '⛅',
  pluvieux: '🌧️', pluie: '🌧️', rain: '🌧️', shower: '🌧️',
  neigeux: '❄️', neige: '❄️', snow: '❄️',
  orageux: '⛈️', orage: '⛈️', thunder: '⛈️', storm: '⛈️',
  brumeux: '🌫️', brume: '🌫️', fog: '🌫️', mist: '🌫️',
};

const HELP_DISMISS_KEY = (uid) => `calendar_help_dismissed_${uid || 'guest'}`;
const ENTRIES_CREATED_KEY = (uid) => `calendar_first_added_${uid || 'guest'}`;
// v1.12.238 — snapshots locaux du look complet (items image+name+brand) pour
// permettre l'affichage détaillé de la tenue en vue jour. La DB ne stocke que
// les références (item_id), donc on garde une copie locale.
const SNAPSHOTS_KEY = (uid) => `calendar_snapshots_${uid || 'guest'}`;
const LOOKS_IA_KEY  = (uid) => `outfit_looks_ia_${uid || 'guest'}`;

// ─── Helpers ────────────────────────────────────────────────────────────
function lc(s) { return String(s || '').toLowerCase().trim(); }

function getTierConfig(tier) {
  return TIER_LIMITS[tier] || TIER_LIMITS.free;
}

function pad2(n) { return n < 10 ? `0${n}` : `${n}`; }
function dateKey(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function dayOfWeekFR(date) {
  const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  const d = date instanceof Date ? date : new Date(date);
  return days[d.getDay()] || 'jour';
}

function occasionDisplay(occ) {
  if (!occ) return OCCASION_DISPLAY.autre;
  return OCCASION_DISPLAY[lc(occ)] || OCCASION_DISPLAY.autre;
}

function weatherIconFromCondition(cond) {
  if (!cond) return null;
  const c = lc(cond);
  for (const [key, ico] of Object.entries(WEATHER_ICONS)) {
    if (c.includes(key)) return ico;
  }
  return null;
}

// ─── 2. CHECK_DATE_CONFLICT ─────────────────────────────────────────────
export async function checkDateConflict({ date, authToken }) {
  if (!date || !authToken) {
    return { success: false, action: 'CHECK_DATE_CONFLICT', errors: [{ code: 'INVALID_DATE', message: 'Date manquante' }] };
  }
  try {
    const month = date.slice(0, 7); // YYYY-MM
    const r = await listMonth({ month, authToken });
    const entries = r?.entries || r?.data?.entries || [];
    const existing = entries.find(e => e.date === date);
    return {
      success: true,
      action: 'CHECK_DATE_CONFLICT',
      data: existing
        ? {
            has_conflict: true,
            existing_entry: {
              id: existing.id,
              date: existing.date,
              name: existing.name || existing.notes || 'Tenue',
              items_count: (existing.items || []).length || existing.items_count || 0,
              composite_image_url: existing.composite_image_url || existing.user_photo_url || null,
              source: existing.source || 'manual',
              occasion: existing.occasion || null,
            },
          }
        : { has_conflict: false, existing_entry: null },
      errors: [],
    };
  } catch (e) {
    return { success: false, action: 'CHECK_DATE_CONFLICT', data: null,
      errors: [{ code: 'INVALID_DATE', message: e?.message || 'Erreur fetch' }] };
  }
}

// ─── 1. ADD_OUTFIT_TO_CALENDAR (avec gestion conflit) ───────────────────
export async function addOutfitToCalendar({ payload, photoUri, authToken, replace = false, user }) {
  if (!authToken) {
    return { success: false, action: 'ADD_OUTFIT_TO_CALENDAR', data: null,
      errors: [{ code: 'USER_NOT_FOUND', message: 'Token manquant' }] };
  }
  // Validations basiques
  const date = payload?.date;
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return { success: false, action: 'ADD_OUTFIT_TO_CALENDAR', data: null,
      errors: [{ code: 'INVALID_DATE', message: 'Date invalide' }] };
  }
  // v1.12.939 — Limite future TIER-AWARE (avant : 7j codés en dur pour TOUS,
  // ce qui bloquait même premium/gold). free=7j · basic=30j · premium/gold=illimité.
  const today = new Date();
  const target = new Date(date);
  const diffDays = (target - today) / (1000 * 60 * 60 * 24);
  const maxFutureDays = getTierConfig(user?.tier).history_days; // Infinity pour premium/gold
  if (Number.isFinite(maxFutureDays) && diffDays > maxFutureDays) {
    return { success: false, action: 'ADD_OUTFIT_TO_CALENDAR', data: null,
      errors: [{ code: 'INVALID_DATE', message: `Date trop loin dans le futur (max ${maxFutureDays} jours pour votre formule)` }] };
  }
  // Items
  const items = Array.isArray(payload.items) ? payload.items : [];
  if (items.length > 12) {
    return { success: false, action: 'ADD_OUTFIT_TO_CALENDAR', data: null,
      errors: [{ code: 'TOO_MANY_ITEMS', message: 'Max 12 items par tenue' }] };
  }
  // Rating
  if (payload.rating != null && (payload.rating < 1 || payload.rating > 5)) {
    return { success: false, action: 'ADD_OUTFIT_TO_CALENDAR', data: null,
      errors: [{ code: 'INVALID_RATING', message: 'Rating doit être entre 1 et 5' }] };
  }

  // Check conflit (sauf si replace=true)
  if (!replace) {
    const conflict = await checkDateConflict({ date, authToken });
    if (conflict.success && conflict.data?.has_conflict) {
      return {
        success: false,
        action: 'ADD_OUTFIT_TO_CALENDAR',
        data: null,
        errors: [{
          code: 'DATE_CONFLICT',
          message: 'Une tenue existe déjà pour cette date',
          existing_entry: conflict.data.existing_entry,
          action_required: 'CONFIRM_REPLACE',
          options: [
            { value: 'replace', label: 'Remplacer la tenue existante' },
            { value: 'cancel', label: 'Garder l\'existante (annuler)' },
          ],
        }],
      };
    }
  } else {
    // Si replace : trouver et supprimer l'existant d'abord
    const conflict = await checkDateConflict({ date, authToken });
    if (conflict.success && conflict.data?.existing_entry?.id) {
      try { await deleteEntry({ id: conflict.data.existing_entry.id, authToken }); } catch {}
    }
  }

  // Créer
  try {
    const result = await addEntryWithOptionalPhoto({
      payload, photoUri, authToken,
    });
    const entryId = result?.id || result?.entry?.id || result?.data?.id || null;

    // Marquer "déjà ajouté au moins une fois" pour le help banner
    try {
      const uid = user?.id || 'guest';
      await AsyncStorage.setItem(ENTRIES_CREATED_KEY(uid), '1');
    } catch {}

    // Insights basiques
    const insights = ['✨ Tenue ajoutée avec succès !'];
    if (items.length > 0) {
      const cats = new Set(items.map(it => lc(it.category)).filter(Boolean));
      if (cats.has('robes')) insights.push('👗 Robe enregistrée pour ce jour');
      else if (cats.has('manteaux')) insights.push('🧥 Tenue avec manteau enregistrée');
    }
    const occ = lc(payload.occasion);
    const occDisp = occ ? occasionDisplay(occ) : null;

    return {
      success: true,
      action: 'ADD_OUTFIT_TO_CALENDAR',
      data: {
        calendar_entry_id: entryId,
        date,
        items_count: items.length,
        composite_image_url: result?.composite_image_url || result?.user_photo_url || null,
        display_data: {
          occasion_emoji:   occDisp?.emoji || '✨',
          occasion_color:   occDisp?.color || '#9B7B5A',
          weather_icon:     weatherIconFromCondition(payload?.weather?.condition) || null,
          rating_stars:     payload.rating || null,
        },
      },
      insights,
      errors: [],
      warnings: [],
    };
  } catch (e) {
    return { success: false, action: 'ADD_OUTFIT_TO_CALENDAR', data: null,
      errors: [{ code: 'INVALID_LOOK_DATA', message: e?.message || 'Échec création' }] };
  }
}

// ─── 3. GET_CALENDAR_MONTH_VIEW ─────────────────────────────────────────
export async function getCalendarMonthView({ year, month, authToken, user, includeStats = true }) {
  if (!authToken) {
    return { success: false, action: 'GET_CALENDAR_MONTH_VIEW',
      errors: [{ code: 'USER_NOT_FOUND', message: 'Token manquant' }] };
  }
  const m = `${year}-${pad2(month)}`;
  try {
    const r = await listMonth({ month: m, authToken });
    const rawEntries = r?.entries || r?.data?.entries || [];

    const today = new Date();
    const todayKey = dateKey(today);

    // Enrichir chaque entrée
    const entries = rawEntries.map(e => {
      const occ = lc(e.occasion);
      const occDisp = occasionDisplay(occ);
      const weatherIcon = weatherIconFromCondition(e.weather_condition || e.weather?.condition);
      return {
        ...e,
        date: e.date,
        day_of_week: dayOfWeekFR(e.date),
        calendar_entry_id: e.id,
        composite_image_url: e.composite_image_url || e.user_photo_url || null,
        items_count: (e.items || []).length || e.items_count || 0,
        occasion: e.occasion,
        occasion_display: {
          emoji: occDisp.emoji,
          color: occDisp.color,
          bg: occDisp.bg,
          label: occDisp.label,
        },
        weather: {
          temp_celsius: e.weather_temp_celsius != null ? e.weather_temp_celsius : (e.weather?.temp_celsius || null),
          icon: weatherIcon || e.weather_icon || null,
          condition: e.weather_condition || e.weather?.condition || null,
        },
        rating: e.rating || null,
        is_today: e.date === todayKey,
      };
    });

    // Stats (si tier le permet)
    const tier = user?.tier || 'free';
    const cfg = getTierConfig(tier);
    const stats = includeStats && cfg.stats ? computeMonthStats(entries, year, month) : null;

    return {
      success: true,
      action: 'GET_CALENDAR_MONTH_VIEW',
      data: {
        month: m,
        days_total: new Date(year, month, 0).getDate(),
        entries,
        stats,
      },
      errors: [],
    };
  } catch (e) {
    return { success: false, action: 'GET_CALENDAR_MONTH_VIEW', data: null,
      errors: [{ code: 'INVALID_DATE', message: e?.message || 'Erreur fetch' }] };
  }
}

// ─── 7. GET_MONTH_STATS (helper interne + export public) ────────────────
export function computeMonthStats(entries, year, month) {
  const daysTotal = new Date(year, month, 0).getDate();
  const daysTracked = entries.length;
  const coveragePct = daysTotal > 0 ? Math.round((daysTracked / daysTotal) * 100) : 0;
  const ratings = entries.map(e => e.rating).filter(r => typeof r === 'number');
  const avgRating = ratings.length > 0
    ? Math.round((ratings.reduce((s, r) => s + r, 0) / ratings.length) * 10) / 10
    : null;

  // Top occasions
  const occMap = new Map();
  for (const e of entries) {
    if (!e.occasion) continue;
    const k = lc(e.occasion);
    occMap.set(k, (occMap.get(k) || 0) + 1);
  }
  const topOccasions = Array.from(occMap.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([name, count]) => {
      const disp = occasionDisplay(name);
      return {
        name, count, emoji: disp.emoji,
        percentage: daysTracked > 0 ? Math.round((count / daysTracked) * 100) : 0,
      };
    });

  // Top items portés
  const itemMap = new Map(); // item_id → { name, count }
  for (const e of entries) {
    for (const it of (e.items || [])) {
      const id = it.item_id || it.id;
      if (!id) continue;
      const key = String(id);
      const prev = itemMap.get(key) || { name: it.name || it.brand || 'Article', times_worn: 0, category: it.category };
      prev.times_worn++;
      itemMap.set(key, prev);
    }
  }
  const topItemsWorn = Array.from(itemMap.entries())
    .map(([id, v]) => ({ item_id: id, ...v }))
    .sort((a, b) => b.times_worn - a.times_worn)
    .slice(0, 5);

  // Underused : pas dans entries du mois (simulé : si on n'a pas la liste complète, on skip)
  // Weather distribution
  const weatherDist = { sunny: 0, cloudy: 0, rainy: 0, snowy: 0, cold: 0 };
  for (const e of entries) {
    const c = lc(e.weather?.condition || e.weather_condition);
    if (/soleil|sun/.test(c)) weatherDist.sunny++;
    else if (/pluie|rain/.test(c)) weatherDist.rainy++;
    else if (/neige|snow/.test(c)) weatherDist.snowy++;
    else if (/nuag|cloud/.test(c)) weatherDist.cloudy++;
    if (typeof e.weather?.temp_celsius === 'number' && e.weather.temp_celsius < 5) weatherDist.cold++;
  }

  // Insights
  const insights = [];
  if (coveragePct >= 70) insights.push(`📊 Vous suivez ${coveragePct}% de vos jours — excellent !`);
  else if (coveragePct >= 40) insights.push(`📊 Vous suivez ${coveragePct}% de vos jours ce mois`);
  else if (daysTracked > 0) insights.push(`📊 ${daysTracked} jour${daysTracked > 1 ? 's' : ''} suivi${daysTracked > 1 ? 's' : ''} ce mois`);
  if (topOccasions[0]) {
    const o = topOccasions[0];
    insights.push(`${o.emoji} ${o.percentage}% de vos tenues sont pour ${o.name}`);
  }
  if (topItemsWorn[0] && topItemsWorn[0].times_worn >= 3) {
    insights.push(`👕 "${topItemsWorn[0].name}" est votre pièce favorite (${topItemsWorn[0].times_worn}× ce mois)`);
  }
  if (avgRating != null && avgRating >= 4) insights.push(`⭐ Note moyenne : ${avgRating}/5 — vous adorez vos tenues`);

  return {
    days_tracked: daysTracked,
    days_total: daysTotal,
    coverage_percentage: coveragePct,
    avg_rating: avgRating,
    top_occasions: topOccasions,
    top_items_worn: topItemsWorn,
    weather_distribution: weatherDist,
    insights,
  };
}

export async function getMonthStats({ year, month, authToken, user }) {
  const tier = user?.tier || 'free';
  const cfg = getTierConfig(tier);
  if (!cfg.stats) {
    return {
      success: false, action: 'GET_MONTH_STATS', data: null,
      errors: [{ code: 'TIER_LIMIT_EXCEEDED', message: 'Stats réservées aux abonnés Basic+' }],
    };
  }
  const view = await getCalendarMonthView({ year, month, authToken, user, includeStats: true });
  if (!view.success) return { ...view, action: 'GET_MONTH_STATS' };
  return {
    success: true,
    action: 'GET_MONTH_STATS',
    data: { month: `${year}-${pad2(month)}`, ...view.data.stats },
    errors: [],
  };
}

// ─── 4. GET_DAY_DETAIL ──────────────────────────────────────────────────
export async function getDayDetail({ entryId, authToken }) {
  if (!entryId || !authToken) {
    return { success: false, action: 'GET_DAY_DETAIL',
      errors: [{ code: 'INVALID_LOOK_DATA', message: 'entryId manquant' }] };
  }
  try {
    const e = await getEntryDetail({ id: entryId, authToken });
    const entry = e?.entry || e?.data || e;
    if (!entry) {
      return { success: false, action: 'GET_DAY_DETAIL', data: null,
        errors: [{ code: 'INVALID_LOOK_DATA', message: 'Entrée introuvable' }] };
    }
    const occDisp = occasionDisplay(entry.occasion);
    return {
      success: true,
      action: 'GET_DAY_DETAIL',
      data: {
        ...entry,
        occasion_display: occDisp,
        weather_icon: weatherIconFromCondition(entry.weather_condition || entry.weather?.condition),
      },
      errors: [],
    };
  } catch (e) {
    return { success: false, action: 'GET_DAY_DETAIL', data: null,
      errors: [{ code: 'INVALID_LOOK_DATA', message: e?.message }] };
  }
}

// ─── 5/6. UPDATE / DELETE ───────────────────────────────────────────────
export async function updateCalendarEntry({ id, patch, authToken }) {
  try {
    const r = await updateEntry({ id, patch, authToken });
    return { success: true, action: 'UPDATE_CALENDAR_ENTRY', data: r, errors: [] };
  } catch (e) {
    return { success: false, action: 'UPDATE_CALENDAR_ENTRY', data: null,
      errors: [{ code: 'INVALID_LOOK_DATA', message: e?.message }] };
  }
}

export async function deleteCalendarEntry({ id, authToken }) {
  try {
    await deleteEntry({ id, authToken });
    return { success: true, action: 'DELETE_CALENDAR_ENTRY', data: { id }, errors: [] };
  } catch (e) {
    return { success: false, action: 'DELETE_CALENDAR_ENTRY', data: null,
      errors: [{ code: 'INVALID_LOOK_DATA', message: e?.message }] };
  }
}

// ─── 8. GENERATE_TOOLTIP_CONTENT ────────────────────────────────────────
export function generateTooltipContent({ context = 'add_to_calendar', tier = 'free' }) {
  const cfg = getTierConfig(tier);
  const tooltips = {
    add_to_calendar: cfg.stats
      ? {
          title: 'Sauvegardez cette tenue dans votre calendrier',
          description: 'Suivez ce que vous portez chaque jour',
          benefits: [
            '✓ Suivre ce que vous portez chaque jour',
            '✓ Éviter les répétitions',
            '✓ Stats personnalisées',
            '✓ Inspiration historique',
          ],
          icon: '💡', position: 'top', max_width: 280,
        }
      : {
          title: 'Ajouter au calendrier',
          description: 'Mémoriser cette tenue',
          benefits: [
            '✓ Suivre vos tenues',
            '✓ Éviter les répétitions',
          ],
          icon: '💡', position: 'top', max_width: 240,
        },
    daily_outfit: {
      title: 'Tenue du jour',
      description: 'La meilleure recommandation IA selon vos préférences et la météo',
      benefits: [
        '✓ Personnalisé à votre style',
        '✓ Adapté à la météo',
        '✓ Régénérable à volonté',
      ],
      icon: '✨', position: 'top', max_width: 280,
    },
  };
  return {
    success: true,
    action: 'GENERATE_TOOLTIP_CONTENT',
    data: { tooltip: tooltips[context] || tooltips.add_to_calendar },
    errors: [],
  };
}

// ─── 9. SHOW_FIRST_TIME_HELP ────────────────────────────────────────────
export async function shouldShowFirstTimeHelp({ userId }) {
  if (!userId) return { success: true, action: 'SHOW_FIRST_TIME_HELP', data: { should_show: false }, errors: [] };
  try {
    const dismissed = await AsyncStorage.getItem(HELP_DISMISS_KEY(userId));
    const everAdded = await AsyncStorage.getItem(ENTRIES_CREATED_KEY(userId));
    if (dismissed === '1') return { success: true, action: 'SHOW_FIRST_TIME_HELP', data: { should_show: false }, errors: [] };
    if (everAdded === '1') return { success: true, action: 'SHOW_FIRST_TIME_HELP', data: { should_show: false }, errors: [] };
    return {
      success: true,
      action: 'SHOW_FIRST_TIME_HELP',
      data: {
        should_show: true,
        reason: 'User has never added an outfit to calendar before',
        banner: {
          title: '💡 Première fois ici ?',
          description: 'Ajoutez les looks que vous aimez à votre calendrier de tenues pour suivre ce que vous portez chaque jour et recevoir des stats personnalisées.',
          benefits: [
            '✓ Évite les répétitions involontaires',
            '✓ Stats personnalisées sur vos préférences',
            '✓ Inspiration depuis votre historique',
            '✓ Synchronisé avec la météo',
          ],
          cta: 'Compris !',
          dismissible: true,
          persist_dismiss: true,
        },
      },
      errors: [],
    };
  } catch {
    return { success: true, action: 'SHOW_FIRST_TIME_HELP', data: { should_show: false }, errors: [] };
  }
}

export async function dismissFirstTimeHelp({ userId }) {
  try { await AsyncStorage.setItem(HELP_DISMISS_KEY(userId || 'guest'), '1'); } catch {}
}

// ─── 10. GENERATE_OUTFIT_PREVIEW_IMAGE (collage frontend) ───────────────
/**
 * Génère un objet "preview" qui décrit comment afficher la tenue. Pour l'instant
 * on retourne juste les URLs des thumbs des items (collage CSS côté UI).
 * Plus tard : Lambda Sharp pour un vrai composite.
 */
export function generateOutfitPreviewImage({ items = [] }) {
  const validImages = items
    .map(it => it.image_url || it.image)
    .filter(u => typeof u === 'string' && /^https?:\/\//.test(u))
    .slice(0, 4);
  return {
    success: true,
    action: 'GENERATE_OUTFIT_PREVIEW_IMAGE',
    data: {
      composite_image_url: null,        // pas de Lambda Sharp pour l'instant
      thumbs: validImages,              // 1 à 4 URLs pour collage CSS
      placeholder_emoji: '👗',
      style: 'frontend_collage',
    },
    errors: [],
  };
}

// ─── Helpers UI exports ─────────────────────────────────────────────────
export function getOccasionDisplay(occ) { return occasionDisplay(occ); }
export function getWeatherIcon(cond)    { return weatherIconFromCondition(cond); }

// ─── v1.12.238 — Snapshots locaux du look (pour affichage détaillé) ─────
/**
 * Sauvegarde la composition complète d'un look dans AsyncStorage, keyed by date.
 * { [date]: { items: [{ id, name, brand, image_url, category }], source, photo } }
 */
export async function saveCalendarSnapshot({ userId, date, look, source, photo }) {
  if (!userId || !date) return;
  try {
    const raw = await AsyncStorage.getItem(SNAPSHOTS_KEY(userId));
    const map = raw ? (JSON.parse(raw) || {}) : {};
    const snapshotItems = (look?.items || []).slice(0, 12).map(it => ({
      id: it.id || null,
      name: it.name || null,
      brand: it.brand || null,
      image_url: it.image_url || it.image || null,
      category: it.category || null,
    }));
    map[date] = {
      source: source || 'manual',
      look_name: look?.name || null,
      photo: photo || null,
      items: snapshotItems,
      saved_at: Date.now(),
    };
    // GC : retirer entries plus vieilles que 90 jours
    const cutoff = Date.now() - 90 * 24 * 3600 * 1000;
    for (const [d, v] of Object.entries(map)) {
      if (v?.saved_at && v.saved_at < cutoff) delete map[d];
    }
    await AsyncStorage.setItem(SNAPSHOTS_KEY(userId), JSON.stringify(map));
  } catch (e) {
    console.warn('[CalendarAgent] saveSnapshot failed:', e?.message);
  }
}

export async function getCalendarSnapshot({ userId, date }) {
  if (!userId || !date) return null;
  try {
    const raw = await AsyncStorage.getItem(SNAPSHOTS_KEY(userId));
    const map = raw ? (JSON.parse(raw) || {}) : {};
    return map[date] || null;
  } catch { return null; }
}

// ─── v1.12.238 — Persist Looks IA pour ajout depuis calendrier ──────────
export async function saveLooksIASnapshot({ userId, looks }) {
  if (!userId) return;
  // v1.12.469 — limites resserrées pour éviter QuotaExceeded sur localStorage web
  // (~5MB partagé entre TOUTES les clés, vs AsyncStorage natif RN ~6MB par clé)
  const isWeb = typeof window !== 'undefined' && !!window.localStorage;
  const MAX_LOOKS = isWeb ? 60 : 250;
  const MAX_ITEMS_PER_LOOK = isWeb ? 6 : 8;
  const MAX_BYTES = isWeb ? 1.5 * 1024 * 1024 : 3 * 1024 * 1024;

  try {
    // v1.12.262 — Strip base64 (data:...) qui peut faire crasher AsyncStorage iOS
    const safeUrl = (u) => (typeof u === 'string' && !u.startsWith('data:')) ? u : null;
    const slim = (looks || []).slice(0, MAX_LOOKS).map(o => ({
      id: o.id || `looks_ia_${Math.random().toString(36).slice(2, 8)}`,
      name: o.name || (o.bestTag ? `Look ${o.bestTag}` : 'Look IA'),
      occasion: o.bestTag || o.occasion || null,
      score: o.score || null,
      items: (o.items || []).slice(0, MAX_ITEMS_PER_LOOK).map(it => ({
        id: it.id, name: it.name, brand: it.brand,
        image_url: safeUrl(it.image_url) || safeUrl(it.image),
        category: it.category,
        pseudo: it.ai_metadata?.pseudo || it.pseudo || null,
      })),
    }));
    let payload = JSON.stringify(slim);
    if (payload.length > MAX_BYTES) {
      const half = slim.slice(0, Math.max(30, Math.floor(slim.length / 2)));
      payload = JSON.stringify(half);
      console.warn('[CalendarAgent] saveLooksIA: payload too big, truncated to', half.length);
    }
    try {
      await AsyncStorage.setItem(LOOKS_IA_KEY(userId), payload);
    } catch (quotaErr) {
      // Retry une seule fois avec encore moins de looks si quota dépassé
      const isQuota = /quota|exceeded/i.test(quotaErr?.message || '') || quotaErr?.code === 22;
      if (isQuota) {
        const tiny = slim.slice(0, 20);
        try {
          await AsyncStorage.setItem(LOOKS_IA_KEY(userId), JSON.stringify(tiny));
          console.warn('[CalendarAgent] saveLooksIA: retry with 20 looks after quota');
        } catch {}
      } else {
        throw quotaErr;
      }
    }
  } catch (e) {
    console.warn('[CalendarAgent] saveLooksIA failed:', e?.message);
  }
}

export async function getLooksIASnapshot({ userId }) {
  if (!userId) return [];
  try {
    const raw = await AsyncStorage.getItem(LOOKS_IA_KEY(userId));
    return raw ? (JSON.parse(raw) || []) : [];
  } catch { return []; }
}
